import {Button, DataStatus, Separator} from 'platform/components';
import {Box, Heading, Hide, HStack, Show, Stack, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {useEffect, useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty, isPositive} from 'ramda-adjunct';

import {useGetCarVerticalConfigQuery, useGetTenantQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {useCreateCarVerticalReportMutation, useGetCarVerticalReportsQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {DEFAULT_COUNTRY, getNaturalPersonFullName, handleApiError} from '@dms/shared';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {CarVerticalLine} from './CarVerticalLine';
import {CarVerticalLogo} from './CarVerticalLogo';

const POLLING_INTERVAL = 10000;

export interface CarVerticalWidgetProps extends TestIdProps {
  vehicleId?: string;
}

export function CarVerticalWidget(props: CarVerticalWidgetProps) {
  const formatDateTime = useDateTimeFormatter();
  const [pollingInterval, setPollingInterval] = useState<number | undefined>(undefined);

  const {data: tenant} = useGetTenantQuery();
  const country = tenant?.country ?? DEFAULT_COUNTRY;

  const {data: carVerticalConfig} = useGetCarVerticalConfigQuery();

  const {
    data: reports,
    isLoading: isReportsLoading,
    isError: isReportsError,
  } = useGetCarVerticalReportsQuery(
    {vehicleId: props.vehicleId!},
    {
      skip: isNil(props.vehicleId) || !carVerticalConfig?.enabled,
      pollingInterval,
    }
  );

  useEffect(() => {
    setPollingInterval(
      reports?.some((report) => report.status === 'in_progress') ? POLLING_INTERVAL : undefined
    );
  }, [reports]);

  const [lastReport, ...olderReports] = reports ?? [];

  const {data: user} = useGetUserQuery(
    {id: lastReport?.createdBy ?? ''},
    {skip: isNilOrEmpty(lastReport?.createdBy) || !carVerticalConfig?.enabled}
  );
  const userFullName = getNaturalPersonFullName(user);

  const [createCarVerticalReport, {isLoading: isCarVerticalReportCreating}] =
    useCreateCarVerticalReportMutation();

  const getNewReport = () => {
    if (carVerticalConfig?.enabled && isNotNil(props.vehicleId)) {
      createCarVerticalReport({
        vehicleId: props.vehicleId,
        createCarVerticalReportRequestBody: {country},
      })
        .unwrap()
        .then((report) => {
          if (isNotNil(report.url)) {
            window.open(report.url, '_blank');
          }
        })
        .catch(handleApiError);
    }
  };

  if (!carVerticalConfig?.enabled) {
    return null;
  }

  return (
    <VStack spacing={2}>
      <Box backgroundColor="palettes.neutral.10.100" borderRadius="medium" overflow="hidden">
        <Stack direction={['column', 'row']}>
          <CarVerticalLogo />
          <Box padding={4} width="100%" backgroundColor="palettes.neutral.10.100">
            <VStack spacing={3}>
              <Heading size={4}>{i18n.t('page.integrations.labels.carVerticalTitle')}</Heading>
              <Hide when={lastReport}>
                <Text size="xSmall" color="secondary">
                  {i18n.t('page.integrations.labels.carVerticalNewReportDescription')}
                </Text>
              </Hide>
              <HStack spacing={2}>
                <Show when={isNotNil(lastReport)}>
                  <Button
                    data-testid={suffixTestId('carVerticalWidget-viewReport', props)}
                    variant="secondary"
                    rightIcon="action/launch"
                    isLoading={lastReport?.status === 'in_progress'}
                    isDisabled={lastReport?.status === 'failed'}
                    onClick={() => isNotNil(lastReport?.url) && window.open(lastReport.url)}
                    title={i18n.t('page.integrations.labels.carVerticalViewReport')}
                  />
                </Show>
                <Button
                  data-testid={suffixTestId('carVerticalWidget-newReport', props)}
                  isLoading={isCarVerticalReportCreating}
                  onClick={getNewReport}
                  variant="secondary"
                  title={i18n.t('page.integrations.labels.carVerticalNewReport')}
                />
              </HStack>
              <Show when={isNotNil(lastReport)}>
                <Text size="xSmall" color="tertiary">
                  {`${i18n.t('page.integrations.labels.carVerticalLastReport')} ${
                    lastReport?.createdAt
                      ? formatDateTime('dateShort', parseDate(lastReport.createdAt))
                      : ''
                  }`}
                  {isNotNilOrEmpty(userFullName) ? `, ${userFullName}` : ''}
                </Text>
              </Show>
              <Show when={isNotNil(lastReport) && lastReport?.status === 'failed'}>
                <Text size="xSmall" color="danger">
                  {i18n.t('page.integrations.labels.reportGenerationFailed')}
                </Text>
              </Show>
              <Show when={isReportsLoading || isReportsError || isPositive(reports?.length)}>
                <Separator spacing={1} />
              </Show>
              <DataStatus
                isLoading={isReportsLoading}
                isError={isReportsError}
                errorMessage={i18n.t('page.integrations.errors.carVerticalReportsLoadError')}
              >
                <VStack spacing={2}>
                  {olderReports.map((report) => (
                    <CarVerticalLine key={report.id} report={report} />
                  ))}
                </VStack>
              </DataStatus>
            </VStack>
          </Box>
        </Stack>
      </Box>
    </VStack>
  );
}
